import DateRangeSelector from '@/components/DateRangeSelector'
import Paper from '@/components/Paper'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import DownLoadIcons from '@/icons/DownLoadIcons'
import {
    formatBandwidthUnit,
    formatNumeral,
    formatRequest,
} from '@/utils/function'
import dayjs from 'dayjs'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Select } from 'vnetwork-ui'
import * as XLSX from 'xlsx'
import { getServiceAnalyticMonitorings } from '../../services'
import { AnalyticQuery, ServiceAnalyticMonitoring } from '../../types'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface VNISCustomerAnalyticsProps {
    productGroupName: string
}

interface CustomerOption {
    value: number
    label: string
}

interface DomainData {
    domain: string
    requestCDN: number
    peakRequestCDN: number
    requestOriginShield: number
    peakRequestOriginShield: number
    totalTrafficCDN: number
    trafficCDN: number
    trafficOriginShield: number
}

interface MonthlyData {
    month: string
    totalSiteFQDN: number
    requestCDN: number
    peakRequestCDN: number
    requestOriginShield: number
    peakRequestOriginShield: number
    totalTrafficCDN: number
    trafficCDN: number
    trafficOriginShield: number
    domains: DomainData[]
}

const VNISCustomerAnalytics: React.FC<VNISCustomerAnalyticsProps> = ({
    productGroupName,
}) => {
    const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
        null
    )
    const [dateRange, setDateRange] = useState({
        gte: dayjs().subtract(6, 'month').startOf('month').format('YYYY-MM-DD'),
        lte: dayjs().endOf('month').format('YYYY-MM-DD'),
    })
    const tableRef = useRef<HTMLDivElement>(null)

    // Fetch customers data to populate dropdown
    const customersQuery: AnalyticQuery = {
        gte: dateRange.gte,
        lte: dateRange.lte,
        product_group_name: productGroupName,
        q: '',
        account_manager_codes: [],
        sale_manager_codes: [],
    }

    const { data: customersData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_ANALYTICS_MONITORING,
            customersQuery,
            'customers-list',
        ],
        queryFn: () => getServiceAnalyticMonitorings(customersQuery),
        enabled: productGroupName === 'VNIS',
    })

    // Get unique customers from the data
    const customerOptions: CustomerOption[] = useMemo(() => {
        if (!customersData?.results) return []

        const uniqueCustomers = new Map<number, string>()
        customersData.results.forEach((item: ServiceAnalyticMonitoring) => {
            if (!uniqueCustomers.has(item.customer_id)) {
                uniqueCustomers.set(item.customer_id, item.customer_name)
            }
        })

        return Array.from(uniqueCustomers.entries()).map(([id, name]) => ({
            value: id,
            label: `[${id}] - ${name}`,
        }))
    }, [customersData])

    // Set first customer as default if none selected
    useEffect(() => {
        if (customerOptions.length > 0 && !selectedCustomerId) {
            setSelectedCustomerId(customerOptions[0].value)
        }
    }, [customerOptions, selectedCustomerId])

    // Fetch data for selected customer
    const customerAnalyticsQuery: AnalyticQuery = {
        gte: dateRange.gte,
        lte: dateRange.lte,
        product_group_name: productGroupName,
        customer_id: selectedCustomerId || undefined,
        q: '',
        account_manager_codes: [],
        sale_manager_codes: [],
    }

    const { data: customerData, isLoading } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.SERVICES_ANALYTICS_MONITORING,
            customerAnalyticsQuery,
            'customer-analytics',
            selectedCustomerId,
        ],
        queryFn: () => getServiceAnalyticMonitorings(customerAnalyticsQuery),
        enabled: !!selectedCustomerId && productGroupName === 'VNIS',
    })

    // Process data by month
    const monthlyAnalytics: MonthlyData[] = useMemo(() => {
        if (!customerData?.results) return []

        const monthlyMap = new Map<string, ServiceAnalyticMonitoring[]>()

        customerData?.results
            ?.filter(
                (item: ServiceAnalyticMonitoring) =>
                    item.customer_id === selectedCustomerId
            )
            ?.forEach((item: ServiceAnalyticMonitoring) => {
                const month = dayjs(item.usage_date).format('MM/YYYY')
                if (!monthlyMap.has(month)) {
                    monthlyMap.set(month, [])
                }
                monthlyMap.get(month)!.push(item)
            })

        return Array.from(monthlyMap.entries())
            .map(([month, data]) => {
                // Group by usage_key, provider, and domain
                const usageMap = new Map<string, number[]>()
                const siteMap = new Set<string>()
                const domainMap = new Map<string, ServiceAnalyticMonitoring[]>()

                data.forEach((item) => {
                    // Collect unique sites/domains
                    if (item.usage_key === 'domains') {
                        siteMap.add(item?.label || '')
                    }

                    // Group by domain for detail rows
                    const domain = item.label || 'Unknown'
                    if (!domainMap.has(domain)) {
                        domainMap.set(domain, [])
                    }
                    domainMap.get(domain)!.push(item)

                    const mapKey = `${item.usage_key}:${item.provider}`
                    if (!usageMap.has(mapKey)) {
                        usageMap.set(mapKey, [])
                    }
                    usageMap.get(mapKey)!.push(item.usage_value)
                })

                const getSum = (key: string, provider: string) => {
                    const mapKey = `${key}:${provider}`
                    const values = usageMap.get(mapKey) || []
                    return values.reduce((sum, val) => sum + val, 0)
                }

                const getPeak = (key: string, provider: string) => {
                    const mapKey = `${key}:${provider}`
                    const values = usageMap.get(mapKey) || []
                    return Math.max(...values, 0)
                }

                // Calculate domain details
                const domainDetails: DomainData[] = Array.from(
                    domainMap.entries()
                )
                    .map(([domain, domainData]) => {
                        const domainUsageMap = new Map<string, number[]>()

                        domainData.forEach((item) => {
                            const mapKey = `${item.usage_key}:${item.provider}`
                            if (!domainUsageMap.has(mapKey)) {
                                domainUsageMap.set(mapKey, [])
                            }
                            domainUsageMap.get(mapKey)!.push(item.usage_value)
                        })

                        const getDomainSum = (
                            key: string,
                            provider: string
                        ) => {
                            const mapKey = `${key}:${provider}`
                            const values = domainUsageMap.get(mapKey) || []
                            return values.reduce((sum, val) => sum + val, 0)
                        }

                        const getDomainPeak = (
                            key: string,
                            provider: string
                        ) => {
                            const mapKey = `${key}:${provider}`
                            const values = domainUsageMap.get(mapKey) || []
                            return Math.max(...values, 0)
                        }

                        return {
                            domain,
                            requestCDN: getDomainSum('requests', 'vncdn'),
                            peakRequestCDN: getDomainPeak('requests', 'vncdn'),
                            requestOriginShield: getDomainSum(
                                'requests',
                                'waf'
                            ),
                            peakRequestOriginShield: getDomainPeak(
                                'requests',
                                'waf'
                            ),
                            totalTrafficCDN:
                                getDomainSum('traffics', 'vncdn') +
                                getDomainSum('traffics', 'waf'),
                            trafficCDN: getDomainSum('traffics', 'vncdn'),
                            trafficOriginShield: getDomainSum(
                                'traffics',
                                'waf'
                            ),
                        }
                    })
                    .sort((a, b) => {
                        // Sort by total traffic + requests (descending)
                        const aTotal =
                            a.totalTrafficCDN +
                            a.requestCDN +
                            a.requestOriginShield
                        const bTotal =
                            b.totalTrafficCDN +
                            b.requestCDN +
                            b.requestOriginShield
                        return bTotal - aTotal
                    })

                return {
                    month,
                    totalSiteFQDN: siteMap.size,
                    requestCDN: getSum('requests', 'vncdn'),
                    peakRequestCDN: getPeak('requests', 'vncdn'),
                    requestOriginShield: getSum('requests', 'waf'),
                    peakRequestOriginShield: getPeak('requests', 'waf'),
                    totalTrafficCDN:
                        getSum('traffics', 'vncdn') + getSum('traffics', 'waf'),
                    trafficCDN: getSum('traffics', 'vncdn'),
                    trafficOriginShield: getSum('traffics', 'waf'),
                    domains: domainDetails,
                }
            })
            .sort(
                (a, b) =>
                    dayjs(`01/${a.month}`, 'DD/MM/YYYY').valueOf() -
                    dayjs(`01/${b.month}`, 'DD/MM/YYYY').valueOf()
            )
    }, [customerData])

    // Export to Excel
    const handleExportExcel = () => {
        if (monthlyAnalytics.length === 0) return

        const selectedCustomer = customerOptions.find(
            (c) => c.value === selectedCustomerId
        )

        const exportData: any[] = []

        monthlyAnalytics.forEach((row) => {
            // Add month summary row
            exportData.push({
                'Month/Domain': row.month,
                'Total Site/FQDN': row.totalSiteFQDN,
                'Request CDN - Total Volume Request Layer 3,4': formatNumeral(
                    row.requestCDN
                ),
                'Peak request cao nhất trong tháng - Peak Request DDoS Protection Layer 3-4':
                    formatNumeral(row.peakRequestCDN),
                'Request Origin Shield - Volume request WAF - origin shield':
                    formatNumeral(row.requestOriginShield),
                'Peak request cao nhất trong tháng - Peak Request DDoS Protection Layer 7- WAF':
                    formatNumeral(row.peakRequestOriginShield),
                'Tổng traffic CDN + Origin Shield - Volume Traffic (GB)':
                    formatNumeral(row.totalTrafficCDN),
                'Traffic CDN - 3,4 (GB)': formatNumeral(row.trafficCDN),
                'Traffic Origin Shield - WAF (GB)': formatNumeral(
                    row.trafficOriginShield
                ),
            })

            // Add domain detail rows
            row.domains.forEach((domain) => {
                exportData.push({
                    'Month/Domain': `  ${domain.domain}`,
                    'Total Site/FQDN': '-',
                    'Request CDN - Total Volume Request Layer 3,4':
                        formatNumeral(domain.requestCDN),
                    'Peak request cao nhất trong tháng - Peak Request DDoS Protection Layer 3-4':
                        formatNumeral(domain.peakRequestCDN),
                    'Request Origin Shield - Volume request WAF - origin shield':
                        formatNumeral(domain.requestOriginShield),
                    'Peak request cao nhất trong tháng - Peak Request DDoS Protection Layer 7- WAF':
                        formatNumeral(domain.peakRequestOriginShield),
                    'Tổng traffic CDN + Origin Shield - Volume Traffic (GB)':
                        formatNumeral(domain.totalTrafficCDN),
                    'Traffic CDN - 3,4 (GB)': formatNumeral(domain.trafficCDN),
                    'Traffic Origin Shield - WAF (GB)': formatNumeral(
                        domain.trafficOriginShield
                    ),
                })
            })
        })

        const ws = XLSX.utils.json_to_sheet(exportData)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Customer Analytics')

        const fileName = `${selectedCustomer?.label || 'Customer'}_Analytics_${dayjs().format('YYYY-MM-DD')}.xlsx`
        XLSX.writeFile(wb, fileName)
    }

    // Export to PDF
    const handleExportPDF = async () => {
        if (!tableRef.current || monthlyAnalytics.length === 0) return

        const selectedCustomer = customerOptions.find(
            (c) => c.value === selectedCustomerId
        )

        try {
            const canvas = await html2canvas(tableRef.current, {
                scale: 2,
                useCORS: true,
                logging: false,
            })

            const imgData = canvas.toDataURL('image/png')
            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4',
            })

            const pdfWidth = pdf.internal.pageSize.getWidth()
            const pdfHeight = pdf.internal.pageSize.getHeight()
            const imgWidth = canvas.width
            const imgHeight = canvas.height
            const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight)
            const imgX = (pdfWidth - imgWidth * ratio) / 2
            const imgY = 10

            pdf.addImage(
                imgData,
                'PNG',
                imgX,
                imgY,
                imgWidth * ratio,
                imgHeight * ratio
            )

            const fileName = `${selectedCustomer?.label || 'Customer'}_Analytics_${dayjs().format('YYYY-MM-DD')}.pdf`
            pdf.save(fileName)
        } catch (error) {
            console.error('Error generating PDF:', error)
        }
    }

    if (productGroupName !== 'VNIS') {
        return null
    }

    return (
        <Paper className="space-y-4">
            <div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="line-clamp-1">
                        <Select
                            allowClear={false}
                            value={selectedCustomerId || ''}
                            onChange={(value) =>
                                setSelectedCustomerId(value as number)
                            }
                            options={customerOptions}
                            placeholder="Select customer"
                        />
                    </div>

                    <div>
                        <DateRangeSelector
                            minDate={'2025-09-01'}
                            value={dateRange}
                            onChange={setDateRange}
                            placement="right"
                        />
                    </div>

                    <div className="flex gap-2">
                        <Button
                            variant="contained"
                            startIcon={<DownLoadIcons />}
                            onClick={handleExportExcel}
                            disabled={monthlyAnalytics.length === 0}
                            className="min-w-[120px]"
                        >
                            Export Excel
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownLoadIcons />}
                            onClick={handleExportPDF}
                            disabled={monthlyAnalytics.length === 0}
                            className="min-w-[120px]"
                        >
                            Export PDF
                        </Button>
                    </div>
                </div>
            </div>
            {/* Data Table */}
            <div ref={tableRef}>
                <Paper className="p-4">
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-gray-300 text-sm">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="border border-gray-300 p-3 text-left font-medium">
                                        Month
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Total Site/FQDN
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Request CDN
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            Total Volume Request Layer 3,4
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Peak request cao nhất trong tháng
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            Peak Request DDoS Protection Layer
                                            3-4
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Request Origin Shield
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            Volume request WAF - origin shield
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Peak request cao nhất trong tháng
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            Peak Request DDoS Protection Layer
                                            7- WAF
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Tổng traffic CDN + Origin Shield
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            Volume Traffic (GB)
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Traffic CDN
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            3,4 (GB)
                                        </span>
                                    </th>
                                    <th className="border border-gray-300 p-3 text-center font-medium">
                                        Traffic Origin Shield
                                        <br />
                                        <span className="text-xs text-gray-600">
                                            WAF (GB)
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td
                                            colSpan={9}
                                            className="border border-gray-300 p-6 text-center text-gray-500"
                                        >
                                            Loading...
                                        </td>
                                    </tr>
                                ) : monthlyAnalytics.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={9}
                                            className="border border-gray-300 p-6 text-center text-gray-500"
                                        >
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {monthlyAnalytics.map((row) => (
                                            <React.Fragment key={row.month}>
                                                <tr className="bg-blue-50 font-semibold">
                                                    <td
                                                        className="border border-gray-300 p-3 font-medium"
                                                        rowSpan={
                                                            row?.domains
                                                                ?.length + 1
                                                        }
                                                    >
                                                        {row.month}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatNumeral(
                                                            row.totalSiteFQDN,
                                                            0
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatRequest(
                                                            row?.requestCDN,
                                                            2,
                                                            'M Request',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatRequest(
                                                            row?.peakRequestCDN,
                                                            2,
                                                            'M Request',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatRequest(
                                                            row?.requestOriginShield,
                                                            2,
                                                            'M Request',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatRequest(
                                                            row?.peakRequestOriginShield,
                                                            2,
                                                            'M Request',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatBandwidthUnit(
                                                            row.totalTrafficCDN,
                                                            0,
                                                            'Gbps',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatBandwidthUnit(
                                                            row.trafficCDN,
                                                            0,
                                                            'Gbps',
                                                            true
                                                        )}
                                                    </td>
                                                    <td className="border border-gray-300 p-3 text-center">
                                                        {formatBandwidthUnit(
                                                            row.trafficOriginShield,
                                                            0,
                                                            'Gbps',
                                                            true
                                                        )}
                                                    </td>
                                                </tr>
                                                {row?.domains?.map((domain) => (
                                                    <tr
                                                        key={`${row.month}-${domain.domain}`}
                                                        className="bg-gray-100"
                                                    >
                                                        <td className="border border-gray-300 p-3 text-center text-sm text-primary">
                                                            {domain.domain}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatRequest(
                                                                domain.requestCDN,
                                                                2,
                                                                'M Request',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatRequest(
                                                                domain.peakRequestCDN,
                                                                2,
                                                                'M Request',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatRequest(
                                                                domain.requestOriginShield,
                                                                2,
                                                                'M Request',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatRequest(
                                                                domain.peakRequestOriginShield,
                                                                2,
                                                                'M Request',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatBandwidthUnit(
                                                                domain.totalTrafficCDN,
                                                                0,
                                                                'Gbps',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatBandwidthUnit(
                                                                domain.trafficCDN,
                                                                0,
                                                                'Gbps',
                                                                true
                                                            )}
                                                        </td>
                                                        <td className="border border-gray-300 p-3 text-center text-sm">
                                                            {formatBandwidthUnit(
                                                                domain.trafficOriginShield,
                                                                0,
                                                                'Gbps',
                                                                true
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                        {/* Total Row */}
                                        <tr className="bg-blue-50 font-semibold">
                                            <td className="border border-gray-300 p-3">
                                                Total
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatNumeral(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            Math.max(
                                                                sum,
                                                                row.totalSiteFQDN
                                                            ),
                                                        0
                                                    ),
                                                    0
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatRequest(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.requestCDN,
                                                        0
                                                    ),
                                                    2,
                                                    'M Request',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatRequest(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.peakRequestCDN,
                                                        0
                                                    ),
                                                    2,
                                                    'M Request',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatRequest(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.requestOriginShield,
                                                        0
                                                    ),
                                                    2,
                                                    'M Request',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatRequest(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.peakRequestOriginShield,
                                                        0
                                                    ),
                                                    2,
                                                    'M Request',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatBandwidthUnit(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.totalTrafficCDN,
                                                        0
                                                    ),
                                                    0,
                                                    'Gbps',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatBandwidthUnit(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.trafficCDN,
                                                        0
                                                    ),
                                                    0,
                                                    'Gbps',
                                                    true
                                                )}
                                            </td>
                                            <td className="border border-gray-300 p-3 text-center">
                                                {formatBandwidthUnit(
                                                    monthlyAnalytics.reduce(
                                                        (sum, row) =>
                                                            sum +
                                                            row.trafficOriginShield,
                                                        0
                                                    ),
                                                    0,
                                                    'Gbps',
                                                    true
                                                )}
                                            </td>
                                        </tr>
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Paper>
            </div>
        </Paper>
    )
}

export default VNISCustomerAnalytics
